/** Official Agent Channels API, distinct from the Bots evaluation/admin family. */
import { createHash } from "node:crypto";
import type { AuthProvider } from "../auth/types.js";
import { RESOURCES } from "../auth/types.js";
import {
  extractPowerPlatformClaims,
  powerPlatformClaimsFailureMessage,
} from "../auth/power-platform-claims.js";
import { parkClaimsForExplicitSignIn, snapshotClaimsBinding } from "../auth/claims-redemption.js";
import {
  ApiError,
  AuthenticationError,
  AuthorizationError,
  RateLimitError,
} from "../utils/errors.js";
import { requestWithRetry } from "../utils/http.js";
import { GUID_PATTERN, validateEnvironmentId, validateRowId } from "../utils/security.js";

export const COPILOT_CHANNEL_ORIGIN = "https://api.powerplatform.com";
export const COPILOT_CHANNEL_MAX_BYTES = 16 * 1024 * 1024;
const CONTEXT = "Download Copilot Studio channel manifest";

export function hasChannelZipSignature(bytes: Buffer): boolean {
  return (
    bytes.length >= 4 &&
    bytes[0] === 0x50 &&
    bytes[1] === 0x4b &&
    ((bytes[2] === 3 && bytes[3] === 4) || (bytes[2] === 5 && bytes[3] === 6))
  );
}

export interface CopilotChannelManifest {
  bytes: Buffer;
  sizeBytes: number;
  sha256: string;
  environmentId: string;
  agentId: string;
  channel: "M365";
  includeAgentSchema: boolean;
}

export class CopilotChannelApi {
  constructor(
    private readonly config: { authProvider: AuthProvider; defaultEnvironmentId: string },
  ) {
    validateEnvironmentId(config.defaultEnvironmentId);
  }

  async downloadManifest(
    agentId: string,
    includeAgentSchema = false,
  ): Promise<CopilotChannelManifest> {
    const id = validateRowId(agentId).toLowerCase();
    const environmentId = validateEnvironmentId(this.config.defaultEnvironmentId);
    if (typeof includeAgentSchema !== "boolean")
      throw new ApiError("includeAgentSchema must be boolean", 400);
    const url =
      `${COPILOT_CHANNEL_ORIGIN}/copilotstudio/environments/${encodeURIComponent(environmentId)}` +
      `/agents/${id}/channels/M365/download?includeAgentSchema=${includeAgentSchema}&api-version=2024-10-01`;
    const signal = AbortSignal.timeout(45_000);
    let token: string;
    try {
      token = await this.config.authProvider.getTokenForResource(RESOURCES.POWER_PLATFORM_API);
      if (!token) throw new Error("empty token");
    } catch {
      throw new AuthenticationError(
        `${CONTEXT}: Power Platform token acquisition failed. Review the existing signed-in session; no permissions were changed.`,
      );
    }
    const headers = { Authorization: `Bearer ${token}`, Accept: "application/zip" };
    const claimsBinding = snapshotClaimsBinding(this.config.authProvider);
    const send = async () => {
      try {
        return await requestWithRetry(
          url,
          { method: "GET", headers, signal, headersTimeout: 30_000, bodyTimeout: 30_000 },
          { maxAttempts: 1 },
        );
      } catch {
        throw new ApiError(
          `${CONTEXT}: HTTP transport failed or timed out; no download saved`,
          502,
        );
      }
    };
    let response = await send();
    const claims =
      response.statusCode === 401 && this.config.authProvider.getTokenForResourceWithClaimsSilent
        ? extractPowerPlatformClaims(response.headers["www-authenticate"])
        : null;
    if (claims !== null) {
      response.body.destroy();
      try {
        const token = await this.config.authProvider.getTokenForResourceWithClaimsSilent!(
          RESOURCES.POWER_PLATFORM_API,
          claims,
        );
        if (!token) throw new AuthenticationError("Claims acquisition returned no token");
        headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        const parked = parkClaimsForExplicitSignIn(
          this.config.authProvider,
          claimsBinding,
          claims,
          CONTEXT,
          error,
        );
        throw new AuthenticationError(
          `${CONTEXT}: ${powerPlatformClaimsFailureMessage(error)}` +
            (parked
              ? " Call sign_in with resource=power_platform_api to attempt explicit same-account device-code redemption, then retry this download. Policy may still deny the request."
              : ""),
        );
      }
      // One claims-aware replay to the same constructed URL; never follow redirects,
      // acquire interactively, change scopes/identity, or retry a second challenge.
      response = await send();
    }
    if (response.statusCode !== 200) {
      response.body.destroy();
      const rawId = response.headers["x-ms-request-id"] ?? response.headers["request-id"];
      const requestId =
        typeof rawId === "string" && GUID_PATTERN.test(rawId) ? ` Request ID: ${rawId}.` : "";
      if (response.statusCode === 401)
        throw new AuthenticationError(
          `${CONTEXT}: Power Platform API authentication failed (HTTP 401).` +
            (claims !== null
              ? " The single silent claims replay was also denied. Token acquisition succeeded, but API access is not verified; no further retry or sign-in was started."
              : " No silent claims replay was performed: a supported unambiguous challenge and claims-capable provider were not both available.") +
            requestId,
        );
      if (response.statusCode === 403)
        throw new AuthorizationError(
          `${CONTEXT}: access denied (HTTP 403). Verify this agent's access, channel availability and the app's existing delegated permissions. The API reference does not name a specific delegated scope; none was inferred or granted.${requestId}`,
        );
      if (response.statusCode === 429) {
        const value = response.headers["retry-after"];
        const seconds =
          typeof value === "string" && /^\d{1,6}$/.test(value) ? Number(value) : undefined;
        throw new RateLimitError(seconds === undefined ? undefined : seconds * 1000);
      }
      throw new ApiError(
        `${CONTEXT}: unexpected HTTP ${response.statusCode}; no download saved.${requestId}`,
        response.statusCode,
      );
    }
    const contentType = response.headers["content-type"];
    if (
      typeof contentType !== "string" ||
      contentType.split(";", 1)[0]?.trim().toLowerCase() !== "application/zip"
    ) {
      response.body.destroy();
      throw new ApiError(`${CONTEXT}: expected application/zip`, 502);
    }
    const rawLength = response.headers["content-length"];
    if (
      rawLength !== undefined &&
      (typeof rawLength !== "string" ||
        !/^\d+$/.test(rawLength) ||
        Number(rawLength) > COPILOT_CHANNEL_MAX_BYTES)
    ) {
      response.body.destroy();
      throw new ApiError(`${CONTEXT}: invalid or oversized Content-Length (maximum 16 MiB)`, 413);
    }
    const chunks: Buffer[] = [];
    let sizeBytes = 0;
    try {
      for await (const chunk of response.body as AsyncIterable<Uint8Array>) {
        sizeBytes += chunk.length;
        if (sizeBytes > COPILOT_CHANNEL_MAX_BYTES)
          throw new ApiError(`${CONTEXT}: exceeds 16 MiB safety limit`, 413);
        chunks.push(Buffer.from(chunk));
      }
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError(`${CONTEXT}: response stream failed or timed out; no download saved`, 502);
    } finally {
      response.body.destroy();
    }
    const bytes = Buffer.concat(chunks);
    if (rawLength !== undefined && Number(rawLength) !== sizeBytes)
      throw new ApiError(`${CONTEXT}: incomplete Content-Length`, 502);
    if (!hasChannelZipSignature(bytes)) {
      throw new ApiError(`${CONTEXT}: response lacks a ZIP signature`, 502);
    }
    return {
      bytes,
      sizeBytes,
      sha256: createHash("sha256").update(bytes).digest("hex"),
      environmentId,
      agentId: id,
      channel: "M365",
      includeAgentSchema,
    };
  }
}
